import * as FlipdishApi from '@flipdish/api-client-typescript';
import axios, { AxiosRequestConfig } from 'axios';
import * as log from 'loglevel';
import objectFitImages from 'object-fit-images';
import * as Raven from 'raven-js';
import * as React from 'react';
import { addTranslationForLanguage, initialize } from 'react-localize-redux';
import { Provider } from 'react-redux';
import { add } from 'react-redux-permissions';
import { Redirect, Route, Router } from 'react-router';
import ResizeObserver from 'resize-observer-polyfill';
import * as BuildConstants from '../BuildConstants';
import * as Actions from './actions/Actions';
import * as analytics from './analytics';
import App from './component/App/index';
import Checkout from './component/Checkout/index';
import CollectionList from './component/CollectionList/index';
import ConcessionList from './component/ConcessionList/index';
import ConfigureItemHolder from './component/ConfigureItemHolder/index';
import CreditCard from './component/CreditCard/index';
import CustomLocation from './component/CustomLocation/index';
import Delivery from './component/Delivery/index';
import DeliveryList from './component/DeliveryList/index';
import Map from './component/Map/index';
import MenuContainer from './component/MenuContainer/index';
import NewLocation from './component/NewLocation/index';
import OrderView from './component/OrderView/index';
import Over18 from './component/Over18/index';
import Payment from './component/Payment/index';
import PhoneNumber from './component/PhoneNumber/index';
import Restaurant from './component/Restaurant/index';
import SetCookie from './component/SetCookie/index';
import SmsCode from './component/SmsCode/index';
import Voucher from './component/Voucher/index';
import Where from './component/Where/index';
import * as Data from './Data';
import * as Intercom from './intercom';
import * as MapLoader from './MapLoader';
import { addMetaData, simulateStartUrl } from './MetaData';
import registerServiceWorker from './registerServiceWorker';
import * as Settings from './SettingsStore';
import * as Store from './Store';
import { StylesProvider, ThemeProvider } from './theme';
import * as Utilities from './Utilities';
import { addToHomescreenInit } from './vendor/AddToHomescreen';
import { history } from './services';
import { getTranslationOrDefault } from '../localization';

const assignWith = require('lodash/assignWith');
const FastClick = require('fastclick');

const isProduction = process.env.NODE_ENV === 'production';
const environment = /master/.test(BuildConstants.GIT_BRANCH) ? 'production' : 'development';
const sentryKey =
  environment === 'production'
    ? 'https://c77a4c3f896c49e1a48855e5a4979fb3@sentry.io/105860'
    : 'https://cd95d48687a54ee1840a16ecef394c93@sentry.io/75499';

const applicationServerPublicKey =
  'BHPXmGnT3r23qsRjaCWNWwXJ4bg7q7aASszB4sGanqscSvr7-MmIZBT6T822KZCQ1t8YadHFYvQNYe7SnpC7yDo';

class Weborder extends React.Component<any, any> {
  private store: any = this.props.store || false;
  private initialState: any = this.props.initialState || false;
  private settings = this.initSettings(this.props.settings || {});

  private initSettings(options) {
    const currentLanguageInStorage = localStorage.getItem('flipdish-current-language');

    if (!currentLanguageInStorage) {
      const initialLanguage = Utilities.extraOption('data-language');
      if (initialLanguage) {
        Utilities.setInLocalStorage('flipdish-current-language', initialLanguage);
      }
    }

    return assignWith(
      // lowest priority
      {
        server: 'https://api.flipdish.co/',
        initialScreen: 'delivery-type',
        fullScreenOnMobile: 'true',
        restaurant: 'system-test-wl',
        theme: 'light',
        offset: '0',
        pwa: 'false',
        initialRoute: false,
        baseHref: '/'
      },

      {
        restaurant: process.env.REACT_APP_APP_NAME_ID
      },

      {
        server: Utilities.extraOption('data-server'),
        initialScreen: Utilities.extraOption('data-initial-screen'),
        fullScreenOnMobile: Utilities.extraOption('data-full-screen-on-mobile'),
        restaurant: Utilities.extraOption('data-restaurant'),
        theme: Utilities.extraOption('data-theme'),
        offset: Utilities.extraOption('data-offset'),
        pwa: Utilities.extraOption('data-pwa'),
        baseHref: Utilities.extraOption('data-base-href')
      },

      // highest priority
      options,

      // customizer
      (objValue: any, srcValue: any) => {
        return !srcValue ? objValue : srcValue;
      }
    );
  }

  private setupRaven() {
    if (isProduction) {
      Raven.config(sentryKey, {
        release: BuildConstants.BUILD_NUM,
        environment,
        instrument: {
          tryCatch: false
        },
        ignoreErrors: [
          'undefined is not an object (evaluating \'jQuery("#map").offset().top\')',
          'Request failed with status code 403',
          'Network Error',
          'Error loading past delivery locations',
          'Error loading collection restaurant',
          'Error loading payment accounts',
          'Order submission failure',
          'Could not load "geocoder"',
          'Could not load "places_impl"',
          'Geolocation error callback',
          "Cannot read property 'top' of undefined",
          'Request failed with status code 500',
          'Error adding card',
          'https://api.flipdish.co/api/events/'
        ]
      }).install();
    }
  }

  private getInitStore(loadedGeo) {
    const readonly = Utilities.extraOption('data-menuid');

    const storeSettings: Data.Config = {
      DeviceId: Utilities.DeviceId(),
      MapLoader: readonly ? null : MapLoader.LoadMaps(),
      showRestaurantList: this.settings.initialScreen === 'delivery-type'
    };

    const { store } = Store.Init(storeSettings, this.initialState);

    if (this.settings.offset != null && !isNaN(parseInt(this.settings.offset, 10))) {
      store.dispatch(Actions.SetOffset(parseInt(this.settings.offset, 10)));
    }

    (window as any)._flipdish = {
      setLanguage: (lang: string) => {
        this.setLanguage(store.getState(), store.dispatch, lang);
      },
      getLanguage: () => {
        const currentLanguageInStorage = localStorage.getItem('flipdish-current-language');
        return currentLanguageInStorage;
      }
    };

    const language = Utilities.extraOption('data-language') || 'en';

    const [localeCode, langJsonWithFallback] = getTranslationOrDefault(language);

    store.dispatch(initialize([localeCode]));
    store.dispatch(
      addTranslationForLanguage(Utilities.translateTransform(langJsonWithFallback), localeCode)
    );

    if (!readonly) {
      store.dispatch(add(FlipdishApi.App.AppResourceSetEnum.UpdateMenu));
      store.dispatch(add(FlipdishApi.App.AppResourceSetEnum.UpdateMenuItemsHideTemporarily));

      store.dispatch(Actions.LoadAppconfig() as any);
      store.dispatch(Actions.LoadCustomer() as any);
      store.dispatch(Actions.SetTheme(this.settings.theme === 'dark'));

      if (loadedGeo) {
        store.dispatch(Actions.AddLocation(loadedGeo, Store.LocationCertainty.Settings));
      }

      Store.watchForLocationChanges(store, (geo) => {
        store.dispatch(Actions.LoadPhoneNumberConfig(geo) as any);
      });
    } else {
      const permissions = Utilities.extraOption('data-permissions');

      if (permissions) {
        const permissionsArray = JSON.parse(permissions);

        for (let permission of permissionsArray) {
          store.dispatch(add(permission));
        }
      }
    }

    return store;
  }

  private setLanguage(store: any, dispatch: any, lang: string) {
    if (
      (window as any)._flipdish &&
      (window as any)._flipdish.getLanguage &&
      (window as any)._flipdish.getLanguage() != lang
    ) {
      const { locale } = store;
      if (locale.languages.some((l) => l.code === lang)) {
        dispatch(Actions.ChangeLanguage(lang));
      }
    }
  }

  private setUserSubscription() {
    registerServiceWorker();
    if ('serviceWorker' in navigator && 'PushManager' in window) {
      (navigator as any).serviceWorker.ready.then((swReg: any) => {
        return swReg.pushManager.getSubscription().then((subscription: any) => {
          const isSubscribed = !(subscription === null);

          if (isSubscribed) {
            log.info('User IS subscribed.');
          } else {
            log.info('User is NOT subscribed.');
            const applicationServerKey = Utilities.urlB64ToUint8Array(applicationServerPublicKey);
            swReg.pushManager
              .subscribe({
                userVisibleOnly: true,
                applicationServerKey
              })
              .then(() => {
                log.info('User is subscribed.');
                // TODO: Update subscription on server
              })
              .catch((err: any) => {
                log.info('Failed to subscribe the user: ', err);
              });
          }
        });
      });
    }
  }

  private setupAxios(loadedGeo) {
    const axiosConfig = (config: AxiosRequestConfig, baseURL: string, whiteLabel: string) => {
      config.headers.common['Flipdish-White-Label-Id'] = whiteLabel;
      config.headers.common['Content-Type'] = 'application/json; charset=utf-8';
      if (loadedGeo) {
        config.headers.common[
          'X-Coordinates'
        ] = `${loadedGeo.Latitude},${loadedGeo.Longitude},${Store.LocationCertainty.Settings}`;
      }
      config.baseURL = baseURL;
      config.withCredentials = true;

      if (Utilities.Android && this.settings.pwa && Utilities.Standalone) {
        config.headers.common['Flipdish-App-Type'] = 'PwaAndroid';
      } else if (Utilities.iOSPhone && this.settings.pwa === 'true' && Utilities.Standalone) {
        config.headers.common['Flipdish-App-Type'] = 'PwaIos';
      } else {
        config.headers.common['Flipdish-App-Type'] = 'Web';
      }

      const flipdishToken = localStorage.getItem('flipdish-token');
      if (flipdishToken) {
        axios.defaults.headers.common['X-Flipdish-Token'] = flipdishToken;
      }

      /*fetch('https://api.ipify.org?format=json')
        .then(res => {
          return res.json();
        })
        .then(json => {
          const flipdishToken = localStorage.getItem(
            `flipdish-token-${json.ip}`
          );
          if (flipdishToken) {
            axios.defaults.headers.common['X-Flipdish-Token'] = flipdishToken;
          }
        })
        .catch(() => {
          const flipdishToken = localStorage.getItem('flipdish-token');
          if (flipdishToken) {
            axios.defaults.headers.common['X-Flipdish-Token'] = flipdishToken;
          }
        });*/
    };

    if (this.settings.restaurant) {
      // TODO - handle the case where whiteLabel is null
      // alert the developer.
      axiosConfig(axios.defaults, this.settings.server, this.settings.restaurant);
    }
  }

  private initDOMandPolyfills() {
    const readonly = Utilities.extraOption('data-menuid');

    if (!readonly && process.env.NODE_ENV !== 'test') {
      try {
        // Create a single ResizeObserver instance to handle all
        // container elements. The instance is created with a callback,
        // which is invoked as soon as an element is observed as well
        // as any time that element's size changes.
        const ro = new ResizeObserver((entries) => {
          const defaultBreakpoints = { XS: 384, SM: 768, MD: 992, LG: 1200 };

          entries.forEach((entry) => {
            // If breakpoints are defined on the observed element,
            // use them. Otherwise use the defaults.
            const breakpoints = (entry.target as any).dataset.breakpoints
              ? JSON.parse((entry.target as any).dataset.breakpoints)
              : defaultBreakpoints;

            // Update the matching breakpoints on the observed element.
            Object.keys(breakpoints).forEach((breakpoint) => {
              const minWidth = breakpoints[breakpoint];
              if (entry.contentRect.width >= minWidth) {
                entry.target.classList.add(breakpoint);
              } else {
                entry.target.classList.remove(breakpoint);
              }
            });
          });
        });

        ro.observe(Utilities.flipdiv);
      } catch (error) {}
    }

    // Run the polyfills for IE
    objectFitImages();

    //For safari 12.2 iframe for the cookies
    const isSafari =
      navigator.vendor && navigator.vendor.indexOf('Apple') > -1 && navigator.userAgent;

    if (isSafari) {
      const iframe = document.createElement('iframe');
      iframe.style.height = '1px';
      iframe.style.width = '1px';
      iframe.style.border = '0';
      iframe.style.position = 'absolute';
      iframe.src = 'https://web-order.flipdish.co/production/embedded/latest/index.html';
      document.body.appendChild(iframe);
    }

    // http://stackoverflow.com/questions/3885018/active-pseudo-class-doesnt-work-in-mobile-safari
    // Add an event listener to force mobile safari to  set the :active class on elements
    // so that we can see buttons being depressed when we click on them.
    document.addEventListener('touchstart', () => {}, false);

    // For iOS bg color on bounce
    if (this.settings.theme === 'dark') {
      document.body.style.backgroundColor = '#414142';
    }

    //If readonly PWA stuff not required
    if (readonly) {
      return;
    }

    // Initialize the Add to Homescreen call out
    addToHomescreenInit();

    // const userAgent = navigator.userAgent;

    // Add manifest
    const manifestExists = document.querySelector("link[rel='manifest']");

    if (!manifestExists) {
      const manifestTag = document.createElement('link');
      manifestTag.href = process.env.PUBLIC_URL + '/manifest.json';
      manifestTag.rel = 'manifest';
      document.head.appendChild(manifestTag);
    }

    // Add theme color meta tag
    const themeMetaExists = document.querySelector("meta[name='theme-color']");

    if (!themeMetaExists) {
      const themeMeta = document.createElement('meta');
      themeMeta.name = 'theme-color';
      themeMeta.content = BuildConstants.THEME_COLOR;
      document.head.appendChild(themeMeta);
    }

    //Add viewport meta tag
    const viewportMetaExists = document.querySelector("meta[name='viewport']");

    if (!viewportMetaExists) {
      const viewportMeta = document.createElement('meta');
      viewportMeta.name = 'viewport';
      viewportMeta.content = 'width=device-width,initial-scale=1,shrink-to-fit=no';
      document.head.appendChild(viewportMeta);
    }

    if (Utilities.iOS || Utilities.Android) {
      addMetaData();

      // Delete all elements
      if (this.settings.fullScreenOnMobile !== 'false' && Utilities.flipdiv) {
        document.body.innerHTML = '';
        document.body.style.backgroundImage = 'none';
        if (Utilities.flipdiv) {
          document.body.appendChild(Utilities.flipdiv);
        }
      }

      if (Utilities.Android) {
        const meta = document.createElement('meta');
        meta.name = 'mobile-web-app-capable';
        meta.content = 'yes';
        document.head.appendChild(meta);
      } else if (Utilities.iOS) {
        if (this.settings.pwa === 'true') {
          FastClick.attach(document.body, {});
        }
      }

      if (!(this.settings.pwa === 'true')) {
        simulateStartUrl();
      } else if (this.settings.pwa === 'true' && Utilities.Standalone) {
        require('./pwa.scss');
      }
    }
  }

  public UNSAFE_componentWillMount() {
    // Print to console build details
    console.log(
      `Branch:${BuildConstants.GIT_BRANCH} Hash:${BuildConstants.GIT_HASH} Build No.:${BuildConstants.BUILD_NUM}`
    );

    if (process.env.REACT_APP_ENV === 'test' || process.env.NODE_ENV === 'test') {
      this.settings.restaurant = 'system-test-wl';
    }

    if (!this.store) {
      const loadedGeo = Settings.LocationStore.Load();

      this.setupRaven();
      this.setUserSubscription();

      this.setupAxios(loadedGeo);
      this.initDOMandPolyfills();
      this.store = this.getInitStore(loadedGeo);
    }
  }

  public componentDidMount() {
    if (this.settings.initialRoute) {
      history.replace(this.settings.initialRoute);
    }
  }

  private logPageView() {
    analytics.PageView();
    if (this.store) {
      Intercom.Update(this.store.getState().intercom);
    }
    Utilities.setInLocalStorage('flipdish-last-url', window.location.hash);

    /*
  const pwaOnMobile =
    (Utilities.Android || Utilities.iOSPhone) &&
    Utilities.Standalone &&
    this.settings.pwa === 'true';

  // Don't store home page as last url (for PWA purposes)
  if (
    window.location.hash != '#/' &&
    window.location.hash.indexOf('#/bounced/') == -1 &&
    pwaOnMobile
  ) {
    Utilities.setInLocalStorage('flipdish-last-url', window.location.hash);
    // 24 hours expiry
    Utilities.setInLocalStorage(
      'flipdish-last-url-expiry',
      (Date.now() + 24 * 60 * 60 * 1000).toString()
    );
  }*/
  }

  public render() {
    const store = this.store;
    const base = this.settings.baseHref;

    const defaultChildren = (
      <Router history={history} onUpdate={this.logPageView}>
        <Route path={base + 'bounced/:bounceurl'} component={App} />
        <Route path={base} component={SetCookie}>
          <Route path="payment/:vrid/:type/:id" component={Payment} />
          <Route path="card/:backurl" component={CreditCard} />
          <Route path="phone(/:dlid)" component={PhoneNumber} />
          <Route path="code/:phone(/:dlid)" component={SmsCode} />
          <Route path="order/:orderId(/:concessionStoreId)" component={OrderView} />
          <Route path="restaurant/:vrid" component={MenuContainer} />
          <Route path="where" component={Where} />
          <Route path="concession/:vrid/:prid" component={ConcessionList} />
          <Route path="collection" component={CollectionList} />
          <Route path="delivery" component={Delivery} />
          <Route path="delivery/new" component={NewLocation} />
          <Route path="delivery/map" component={Map} />
          <Route path="delivery/custom/:lat/:lng" component={CustomLocation} />
          <Route path="delivery/confirm(/:lat/:lng)" component={CustomLocation} />
          <Route path="delivery/:location" component={DeliveryList} />
          <Route path="restaurant/:vrid/:type/:id(/:concessionStoreId)" component={Restaurant} />
          <Route path="over18/:vrid/:type/:id(/:concessionStoreId)" component={Over18} />
          <Route
            path="restaurant/:vrid/:type/:id(/:concessionStoreId)/configure/:item/:index"
            component={ConfigureItemHolder}
          />
          <Route path="checkout/:vrid/:type/:id(/:concessionStoreId)" component={Checkout} />
          <Route
            path="voucher/:vrid/:type/:id/:paymentAccount(/:concessionStoreId)"
            component={Voucher}
          />
        </Route>
        <Redirect from={base + '*'} to={base + 'where'} />
      </Router>
    );

    return (
      <StylesProvider>
        <Provider store={store}>
          <ThemeProvider>{this.props.children || defaultChildren}</ThemeProvider>
        </Provider>
      </StylesProvider>
    );
  }
}

export default Weborder;
